import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useFormik,
} from 'formik';
import * as yup from 'yup';
import {
  Grid,
  Typography,
  FormControl,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';

import { setOnlyUserInfo, useUserInfoState } from '../../../../hooks/userInfo';
import ConfigProjectLayout from '../../../../theme/templates/project/config';
import { LogoCompanySVG } from '../../../../assets/svg';
import { getBase64 } from '../../../../helper/images';
import { UpdateProfileForm } from '../../../../config/interfaces';
import {
  UserNode,
  useUpdateProfileMutation,
} from '../../../../../graphql/generated/graphql';
import JobTitle from '../../../components/job-title';
import AddLogoCompany from '../../../components/company/add-logo';

import './index.scss';

export default function SettingsPersonPage() {

  const navigate = useNavigate();
  const userInfoState = useUserInfoState();
  const userInfo = userInfoState.user.get();
  const [updateProfile] = useUpdateProfileMutation();
  const [loader, setLoader] = useState(false);
  const [editAvatar, setEditAvatar] = useState(false);
  const [image, setImage] = useState(userInfo?.url_avatar ? <img className="image-logo" src={userInfo?.url_avatar} alt="logo" /> : <LogoCompanySVG />);

  const initialValues: UpdateProfileForm = {
    firstName: userInfo?.first_name || '',
    lastName: userInfo?.last_name || '',
    email: userInfo?.email || '',
    jobTitle: userInfo?.job_title?.id || '',
    avatar: userInfo?.url_avatar || '',
  };

  const UpdateProfileSchema = yup.object().shape({
    firstName: yup.string()
      .min(2, 'Too Short!')
      .max(50, 'Too Long!')
      .required('Required'),
    lastName: yup.string()
      .min(2, 'Too Short!')
      .max(50, 'Too Long!')
      .required('Required'),
    email: yup.string()
      .required('Required'),
    jobTitle: yup.string()
      .required('Required')
      .nullable(),
    avatar: yup.string()
      .required('Required'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: UpdateProfileSchema,
    onSubmit: async (variables) => {
      setLoader(true);
      try {
        const response = await updateProfile({
          variables: {
            first_name: variables.firstName,
            last_name: variables.lastName,
            job_title_id: variables.jobTitle,
            avatar: editAvatar ? variables.avatar : undefined,
          },
        });
        navigate('/');
        setOnlyUserInfo(response.data?.updateProfile as UserNode);
        setLoader(true);
      } catch (e) {
        setLoader(false);
      }
      setLoader(false);
    },
  });

  const onLoad = async (file: File) => {
    setEditAvatar(true);
    const objectUrl = URL.createObjectURL(file)
    setImage(<img className="image-logo" src={objectUrl} alt="logo" />);
    getBase64(file)
      .then(async (logo) => {
        formik.setFieldValue('avatar', logo);
      });
  };

  return (
    <ConfigProjectLayout>
      <Typography
        component="h1"
        variant="h3"
        sx={{ fontWeight: 500 }}
      >
        User settings
      </Typography>
      <Grid
        container
        sx={{
          mt: 4,
          width: '100%',
        }}
      >
        <Grid
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <Grid
            item
            sx={{
              width: '194px',
              maxWidth: '90%',
              height: '186px',
            }}
          >
            <AddLogoCompany image={image} onLoad={onLoad} />
          </Grid>
          <Grid
            sx={{
              width: 'calc(100% - 250px)',
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              mt: 1
            }}
          >
            <FormControl
              fullWidth
              component="form"
              onSubmit={formik.handleSubmit}
            >
              <Grid
                container
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                }}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  sx={{
                    width: '40%',
                    maxWidth: '100%',
                    minWidth: '200px',
                    mb: 4,
                    mr: 3
                  }}
                  id="first-name"
                  label="First Name"
                  name="firstName"
                  autoComplete="first-name"
                  autoFocus
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                  helperText={formik.touched.firstName && formik.errors.firstName}
                />
                <TextField
                  margin="normal"
                  required
                  sx={{
                    width: '40%',
                    maxWidth: '100%',
                    minWidth: '200px',
                    mb: 4,
                  }}
                  fullWidth
                  id="last-name"
                  label="Last Name"
                  name="lastName"
                  autoComplete="last-name"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                  helperText={formik.touched.lastName && formik.errors.lastName}
                />
                <TextField
                  disabled
                  margin="normal"
                  sx={{
                    width: '40%',
                    maxWidth: '100%',
                    minWidth: '200px',
                    mb: 4,
                    mt: 0,
                    mr: 3
                  }}
                  required
                  fullWidth
                  id="email"
                  label="Email"
                  name="email"
                  autoComplete="email"
                  value={formik.values.email}
                />
                <FormControl
                  sx={{
                    width: '40%',
                    maxWidth: '100%',
                    minWidth: '200px',
                    mb: 4,
                  }}
                >
                  <JobTitle
                    value={formik.values.jobTitle}
                    formik={formik}
                    error={formik.touched.jobTitle && Boolean(formik.errors.jobTitle)}
                    helperText={formik.touched.jobTitle && formik.errors.jobTitle}
                  />
                </FormControl>
              </Grid>
              <Button
                type="submit"
                variant="contained"
                disabled={!(formik.isValid && formik.dirty)}
                sx={{
                  mt: 1,
                  width: '100px',
                }}
              >
                {loader ? (
                  <CircularProgress color="primary" size={20} />
                ) : (
                  'Save'
                )}
              </Button>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
    </ConfigProjectLayout>
  );
}
